<template>
  <div>
    <div
      class="col-12 col-md-6 pa-0"
      :class="[question.Description ? question.DescriptionClass : 'col-xl-6']"
    >
      <HomeBaseLabel :question="question" />
      <div
        v-if="question.Description"
        class="d-flex align-left flex-no-wrap font-14 description"
      >
        {{ question.Description }}
      </div>

      <div
        :class="{
          'italic-placeholder': question?.PlaceholderText,
          'col-12 col-md-6 pa-0': question?.Description,
        }"
      >
        <BaseAddressLookup
          v-model="question.Value"
          :rules="{
            required: {
              fieldLabel: question.Text,
            },
          }"
          :hideDetails="
            question.HideDetails != null ? question.HideDetails : 'auto'
          "
          @address-selected="addressSelected"
          @click:clear="onClear()"
          @keyup.delete="onClear()"
          :loading="loading"
          @enter-address-manually="enterManually"
          ref="autocomplete"
          :data-uiid="question.dataUiid"
        />
      </div>
    </div>
  </div>
</template>

<script>
import AllQuestionsMixin from "@/Mixins/AllQuestionsMixin";
import InterfaceKeyPrefixMixin from "@/Mixins/InterfaceKeyPrefixMixin";
import LexisNexusLookup from "@/Mixins/PropertyDetails/LexisNexisLookup";
import QuestionValidation from "@/Mixins/Validation/QuestionValidation";

import {
  generateAddress,
  getSelectedAddress,
  loqateEntriesToList,
  resetAddressLayoutAnswers,
  updateAddressLayoutAnswers,
} from "@/common/addresses";
import BaseAddressLookup from "@/components/BaseComponents/BaseAddressLookup";
import HomeBaseLabel from "@/components/HomeComponents/HomeBaseComponents/HomeBaseLabel";
import {
  QUICKQUOTERETRIEVED,
  PROCEEDED,
  QUOTESRETRIEVED,
} from "@/constants/common.constants";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { mapActions, mapGetters, mapState } from "vuex";
export default {
  name: "InputFindPropertyLexisNexis",
  mixins: [
    LexisNexusLookup,
    QuestionValidation,
    AllQuestionsMixin,
    InterfaceKeyPrefixMixin,
  ],
  components: {
    HomeBaseLabel,
    BaseAddressLookup,
  },
  props: {
    question: {
      type: Object,
      required: true,
    },
    isShowCardPropertyDetails: Boolean,
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, [
      "form",
      "quoteResponse",
      "isQuickQuote",
      "questionMap",
      "hasResetQuickQuotePropertyData",
    ]),
    ...mapGetters(QUESTIONS_MODULE, ["getQuoteRequestId", "isSelfServe"]),
    propertyDataLoading() {
      return this.loading ? "info" : false;
    },
    items() {
      return loqateEntriesToList(this.entries);
    },
    getQuoteRequestId() {
      return this.quoteResponse?.QuoteRequestId;
    },
    getQuoteStatus() {
      return this.quoteResponse?.QuoteStatus;
    },
  },
  watch: {
    search(val) {
      this.isLoading = true;
      this.searchAddressDebounced(val);
    },
  },
  mounted() {
    if (this.shouldCheckAndResetData()) {
      this.setPropertyDetailsFormVisibility(false);
      this.propertyDetailsLookup().then(() => {
        Object.entries(this.form.originalQuickQuotePropertyData).forEach(
          ([key, value]) => {
            if (this.questionMap[key]) {
              this.questionMap[key].Value = value;
            }
          }
        );
        this.$emit("emitEditButtonValue", false);
        this.setPropertyDetailsFormVisibility(true);
        this.setPropertyDetailsCardVisibility(false);
        this.updateHasResetQuickQuotePropertyData(true);
      });
    }
  },
  data() {
    return {
      getPostcodeResponse: null,
      loading: false,
    };
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "setPropertyDetailsFormVisibility",
      "updateHasResetQuickQuotePropertyData",
    ]),
    addressSelected(response) {
      const selectedAddress = getSelectedAddress(
        response,
        this.interfaceKeyPrefix
      );

      // remove duplicate address values
      selectedAddress.InsuredAddressCounty =
        selectedAddress.InsuredAddressTown ===
        selectedAddress.InsuredAddressCounty
          ? ""
          : selectedAddress.InsuredAddressCounty;

      const street = selectedAddress.InsuredAddressStreet;
      const town = selectedAddress.InsuredAddressTown;
      const postcode = selectedAddress.InsuredAddressPostCode;
      const displayAddress = generateAddress(street, town, postcode);

      this.question.Value = displayAddress;

      // Update all the address question fields
      this.updateAllAddressQuestions(selectedAddress);

      // Lexis Nexis lookup - see Mixins for definition
      this.propertyDetailsLookup().then(() => {
        this.setPropertyDetailsCardVisibility(true);
      });
    },
    resetAllAddressQuestions() {
      resetAddressLayoutAnswers(
        this.allQuestions,
        "LayoutAddressSearchLexisNexis",
        this.question.InterfaceKey
      );
    },
    updateAllAddressQuestions(selectedAddress) {
      updateAddressLayoutAnswers(
        this.allQuestions,
        "LayoutAddressSearchLexisNexis",
        this.question.InterfaceKey,
        selectedAddress
      );
    },
    setAddressFormVisibility(value) {
      this.$emit("toggleAddressFormVisible", value);
    },
    setPropertyDetailsCardVisibility(value) {
      this.$emit("togglePropertyDetailsCardVisible", value);
    },
    onClear() {
      this.form.isLNErrorMessageEnabled = true;
      this.setPropertyDetailsCardVisibility(false);
      this.setPropertyDetailsFormVisibility(false);
    },
    enterManually() {
      this.resetAllAddressQuestions();
      this.setAddressFormVisibility(true);
      this.$refs.autocomplete.isMenuActive = false;
      this.setPropertyDetailsCardVisibility(false);
    },
    shouldCheckAndResetData() {
      return (
        this.isQuickQuote &&
        this.isSelfServe &&
        !this.hasResetQuickQuotePropertyData &&
        this.isAddressValid() &&
        this.quoteResponse.QuoteStatus === QUOTESRETRIEVED &&
        this.quoteResponse.QuickQuoteStatus &&
        (this.quoteResponse.QuickQuoteStatus === QUICKQUOTERETRIEVED ||
          this.quoteResponse.QuickQuoteStatus === PROCEEDED)
      );
    },
    isAddressValid() {
      return (
        this.questionMap.InsuredAddressPostCode.Value &&
        this.questionMap.InsuredAddressStreet.Value &&
        this.questionMap.InsuredAddressTown.Value
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.description {
  letter-spacing: 0.4px;
  margin-bottom: 13px;
  max-width: 665px;
  color: #272727;
}
</style>
